<template>
  <div v-if="groups && groups.length > 0" class="container mx-auto m-5">
    <Title title="Groups" />

    <div class="grid gap-4">
      <div v-for="group in groups" :key="group.name"
        class="bg-white shadow-lg rounded-sm border border-slate-200 overflow-hidden">
        <!-- Group Header -->
        <div class="bg-[#F8FAFC] border-b border-slate-200">
          <div class="flex items-center justify-between p-5">
            <button @click="toggleServices(group)"
              class="flex items-center gap-3 hover:text-slate-800 transition-colors">
              <div class="w-6 h-6 flex items-center justify-center">
                <svg class="w-4 h-4 text-slate-600 transition-transform duration-200"
                  :class="{ 'rotate-90': group?.showServices }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-slate-800">{{ group?.name }}</h3>
                <p class="text-sm text-slate-600">{{ group?.services?.length || 0 }} services</p>
              </div>
            </button>

            <div class="flex items-center gap-4">
              <div class="text-right">
                <p class="text-sm text-slate-600">Monthly Total</p>
                <p class="text-lg font-semibold text-slate-800">
                  <span v-currency="group.totalMonthlyCost">{{ group.totalMonthlyCost }}</span>
                </p>
              </div>
              <button @click="deleteGroupClick(group)"
                class="p-2 text-red-500 hover:bg-red-50 rounded transition-colors">
                <Trash class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <!-- Services List -->
        <div v-if="group?.showServices" class="divide-y divide-slate-100">
          <div v-for="(service, idx) in group?.services" :key="service.id"
            class="p-4 hover:bg-slate-50 transition-colors">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="w-6 h-6 bg-slate-100 rounded flex items-center justify-center">
                  <span class="text-xs font-medium text-slate-600">{{ idx + 1 }}</span>
                </div>
                <div>
                  <h4 class="font-medium text-slate-800">{{ service?.name }}</h4>
                  <p class="text-sm text-slate-600 line-clamp-1">{{ service?.description || 'No description' }}</p>
                </div>
              </div>

              <div class="flex items-center gap-6">
                <div class="text-right">
                  <p class="text-xs text-slate-500">Monthly</p>
                  <p class="font-medium text-slate-800">
                    <span v-currency="service.monthlyCost">{{ service.monthlyCost }}</span>
                  </p>
                </div>
                <div class="text-right">
                  <p class="text-xs text-slate-500">12-Month</p>
                  <p class="font-medium text-slate-800">
                    <span v-currency="service.totalCost">{{ service.totalCost }}</span>
                  </p>
                </div>
                <button @click="removeServiceFromGroup(group, service)"
                  class="p-1 text-red-500 hover:bg-red-50 rounded transition-colors">
                  <Trash class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div v-if="!group?.services || group?.services.length === 0" class="p-6 text-center text-slate-500">
            <p class="text-sm">No services in this group</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div v-if="deleteGroupModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    @click.self="deleteGroupModal = false">
    <div class="bg-white rounded-sm shadow-lg max-w-md w-full mx-4 border border-slate-200">
      <div class="p-5">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 bg-red-50 rounded flex items-center justify-center">
            <Trash class="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 class="text-lg font-semibold text-slate-800">Delete Group</h3>
            <p class="text-sm text-slate-600">This action cannot be undone</p>
          </div>
        </div>

        <p class="text-sm text-slate-600 mb-6">
          Delete "<strong>{{ selectedGroup.name }}</strong>" and all its services?
        </p>

        <div class="flex gap-3 justify-end">
          <button @click="deleteGroupModal = false"
            class="px-3 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button @click="deleteGroup"
            class="px-3 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors">
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Title from "./Title.vue";
import { Trash } from "lucide-vue-next";

export default {
  components: {
    Title,
    Trash,
  },
  data() {
    return {
      deleteGroupModal: false,
      selectedGroup: {},
    };
  },
  computed: {
    // Totals follow the live lines, so editing a quantity updates its groups.
    ...mapGetters({ groups: "services/groupsWithTotals" }),
  },
  methods: {
    toggleServices(group) {
      if (!group) return;
      this.$store.commit("services/UPDATE_GROUP", {
        ...group,
        services: (group.services || []).map((service) => ({
          id: service.id,
          name: service.name,
        })),
        showServices: !group.showServices,
      });
    },
    deleteGroup() {
      this.$store.commit("services/DELETE_GROUP", this.selectedGroup);
      this.deleteGroupModal = false;
      this.selectedGroup = {};
    },
    deleteGroupClick(group) {
      this.selectedGroup = group;
      this.deleteGroupModal = true;
    },
    removeServiceFromGroup(group, service) {
      this.$store.commit("services/UPDATE_GROUP", {
        ...group,
        services: (group.services || [])
          .filter((entry) => entry.id !== service.id)
          .map((entry) => ({ id: entry.id, name: entry.name })),
      });
    },
  },
};
</script>

<style scoped>
.rotate-90 {
  transform: rotate(90deg);
}
</style>
